import React from 'react';
import { css } from '@emotion/react';
import { Grid, FormControlLabel, Radio, RadioGroup, TextField, Tooltip, IconButton, Switch } from '@material-ui/core';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import { Body, PrimaryButton } from '@/styles/blocks';
import { DatePicker } from '@/components/OrderDetailsPage/Crossdocking/blocks';

import { Body1 } from './blocks';
import {
    REQUIRES_CARRIER_INSTRUCTION,
    ACTION_DISPLAY_MAPPING,
    INSTRUCTIONS_DISPLAY_MAPPING,
    ACTIONS,
} from './constants';
import RescheduleReasonFields from '@/components/Reschedule/RescheduleReasonFields';
import { InfoOutlined } from '@material-ui/icons';

const ResolutionRadioGroup = ({
    resolution,
    instruction,
    exception,
    loading,
    updates,
    tz,
    actions,
    instructions,
    isValid,
    clearPoNumber,
    callbacks,
    route_id,
}) => {
    return (
        <>
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: 20px;
                `}
            >
                <Body1>How would you like to resolve the exception? (Required)</Body1>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: 20px;
                `}
            >
                <RadioGroup
                    onChange={(e) => {
                        callbacks.setAction(e.target.value);
                    }}
                >
                    {actions.map((action) => {
                        return (
                            <Grid container alignItems="center">
                                <FormControlLabel
                                    key={action}
                                    value={action}
                                    control={<Radio checked={resolution === action} color="primary" />}
                                    label={<Body1>{ACTION_DISPLAY_MAPPING[action]}</Body1>}
                                />
                                {action === 'CREATE_RETURN' && (
                                    <Tooltip
                                        style={{ color: '#59B863' }}
                                        title={
                                            "Create a return order where the return pick up is the customer/consignee's address and the drop off is this original order's pick up address."
                                        }
                                        placement="top"
                                    >
                                        <IconButton size="small">
                                            <InfoOutlined />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {action === 'CREATE_WAREHOUSE_RETURN' && (
                                    <Tooltip
                                        style={{ color: '#59B863' }}
                                        title={
                                            "Create a return order where the return origin is set as the carrier's warehouse address and the drop off is the shipping store's preferred inbound location."
                                        }
                                        placement="top"
                                    >
                                        <IconButton size="small">
                                            <InfoOutlined />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Grid>
                        );
                    })}
                </RadioGroup>
            </Grid>
            {[ACTIONS.CREATE_RETURN, ACTIONS.CREATE_WAREHOUSE_RETURN].includes(resolution) ? (
                <Grid
                    container
                    direction="row"
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={clearPoNumber}
                                onChange={(e) => callbacks.setClearPoNumber(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={<Body1>Leave PO# blank for RMA entry</Body1>}
                    />
                </Grid>
            ) : null}
            {resolution === ACTIONS.RESCHEDULE ? (
                <Grid
                    container
                    direction="row"
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <DatePicker
                            dateLabel="New Pickup Date"
                            tz={tz}
                            timestamp={updates.pickup_date}
                            onChange={(date) =>
                                callbacks.setUpdates((prev) => ({
                                    ...prev,
                                    pickup_date: date ? date.toISOString() : null,
                                }))
                            }
                        />
                    </Grid>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                        `}
                    >
                        <DatePicker
                            dateLabel="New Dropoff Date"
                            tz={tz}
                            timestamp={updates.delivery_date}
                            onChange={(date) =>
                                callbacks.setUpdates((prev) => ({
                                    ...prev,
                                    delivery_date: date ? date.toISOString() : null,
                                }))
                            }
                        />
                    </Grid>
                </Grid>
            ) : null}

            {resolution === ACTIONS.RESCHEDULE ? (
                <Grid
                    container
                    direction="row"
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <RescheduleReasonFields
                        reason={updates.reschedule_reason_code}
                        notes={updates.reschedule_reason_notes}
                        onChangeReason={(val) =>
                            callbacks.setUpdates((prev) => ({ ...prev, reschedule_reason_code: val }))
                        }
                        onChangeNotes={(val) =>
                            callbacks.setUpdates((prev) => ({ ...prev, reschedule_reason_notes: val }))
                        }
                        disabled={loading}
                    />
                </Grid>
            ) : null}

            {REQUIRES_CARRIER_INSTRUCTION.includes(exception.type) ? (
                <>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 20px;
                        `}
                    >
                        <Body1>What would you like to do with the items? (Required)</Body1>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 20px;
                        `}
                    >
                        <RadioGroup
                            onChange={(e) => {
                                callbacks.setInstruction(e.target.value);
                            }}
                        >
                            {instructions.map((key) => {
                                return (
                                    <FormControlLabel
                                        key={key}
                                        value={key}
                                        control={<Radio checked={instruction === key} color="primary" />}
                                        label={<Body1>{INSTRUCTIONS_DISPLAY_MAPPING[key]}</Body1>}
                                    />
                                );
                            })}
                        </RadioGroup>
                    </Grid>
                </>
            ) : null}

            {resolution === ACTIONS.RESCHEDULE && exception.type === EXCEPTION_TYPES.ATTEMPTED_DELIVERY ? (
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <Grid item xs={12}>
                        <Body1>Bill this attempt immediately?</Body1>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={updates.bill_attempt ?? true}
                                    color="primary"
                                    onChange={(e) =>
                                        callbacks.setUpdates((prev) => ({ ...prev, bill_attempt: e.target.checked }))
                                    }
                                />
                            }
                            label={
                                <Body1>
                                    Create a billing order for this attempt (PO -A) and bill the attempt fee now
                                </Body1>
                            }
                        />
                    </Grid>
                </Grid>
            ) : null}

            {route_id &&
            exception.route_id &&
            exception.route_id === route_id &&
            [ACTIONS.CANCEL_ORDER, ACTIONS.RESCHEDULE, ACTIONS.RESOLVE_NOOP].includes(resolution) ? (
                <>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 20px;
                        `}
                    >
                        <Grid item xs={12}>
                            <Body1>Would you like to recalculate the route distance with the stop(s) removed?</Body1>
                        </Grid>
                        <Grid item xs={12}>
                            <RadioGroup
                                onChange={(e) => {
                                    callbacks.setUpdates((prev) => ({
                                        ...prev,
                                        recalc_route: e.target.value,
                                    }));
                                }}
                            >
                                <FormControlLabel
                                    value="true"
                                    control={<Radio checked={updates.recalc_route === 'true'} color="primary" />}
                                    label={<Body1>Yes</Body1>}
                                />
                                <FormControlLabel
                                    value="false"
                                    control={<Radio checked={updates.recalc_route === 'false'} color="primary" />}
                                    label={<Body1>No</Body1>}
                                />
                            </RadioGroup>
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 20px;
                        `}
                    >
                        <Grid item xs={12}>
                            <Body1>How would you like to update the route revenue?</Body1>
                        </Grid>
                        <Grid item xs={12}>
                            <RadioGroup
                                onChange={(e) => {
                                    callbacks.setUpdates((prev) => ({
                                        ...prev,
                                        route_rev_action: e.target.value,
                                    }));
                                }}
                            >
                                {[
                                    { value: 'keep', label: 'Keep current revenue' },
                                    { value: 'remove', label: 'Remove revenue for this order' },
                                    { value: 'custom', label: 'New custom charge' },
                                ].map(({ value, label }) => (
                                    <FormControlLabel
                                        key={value}
                                        value={value}
                                        control={<Radio checked={updates.route_rev_action === value} color="primary" />}
                                        label={<Body1>{label}</Body1>}
                                    />
                                ))}
                            </RadioGroup>
                        </Grid>
                        {updates.route_rev_action === 'custom' && (
                            <Grid item xs={12}>
                                <TextField
                                    type="number"
                                    label="Custom charge"
                                    value={updates.custom_charge || ''}
                                    onChange={(e) => {
                                        callbacks.setUpdates((prev) => ({
                                            ...prev,
                                            custom_charge: e.target.value,
                                        }));
                                    }}
                                    css={css`
                                        margin-top: 10px;
                                        margin-bottom: 0px !important;
                                    `}
                                />
                            </Grid>
                        )}
                    </Grid>
                </>
            ) : null}
        </>
    );
};

export default ResolutionRadioGroup;
